import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import UserAvatar from 'treeline-react-core-components/UserAvatar';
import { useComments } from '../containers/commentsDataStore';
import { useConversations } from '../containers/conversationsDataStore';
import ConversationStatus from '../ConversationStatus';
import Comment from './Comment';
import CommentInputFrame from './CommentInputFrame';
import { useCommunityReviews } from '../../Community/containers/CommunityReviewsDataStore';
import useNewReviewCopies from '../../ReviewCopies/hooks/useNewReviewCopies';
import { useRequests } from '../../Requests/containers/RequestsDataStore';
import useCommunityPageParams from '../../hooks/useCommunityPageParams';
import useTitleConversationStatuses from '../../Titles/hooks/useTitleConversationStatuses';
import CacheKeysEnum from '../../../CacheKeysEnum';

ParentCommentFrame.propTypes = {
	comments: PropTypes.array,
	refreshAfterViewing: PropTypes.func,
	markAsRead: PropTypes.func,
};

export default function ParentCommentFrame({
	comments,
	refreshAfterViewing,
	markAsRead,
}) {
	const { communityId } = useCommunityPageParams();
	const maxTitles = communityId ? 20 : 40;
	const commentsDataStore = useComments();
	const conversationsDataStore = useConversations();
	const communityReviewsDataStore = useCommunityReviews();
	const {
		data: { skus },
	} = useNewReviewCopies(communityId ?? 0, maxTitles);
	const requestsDataStore = useRequests();
	const {
		refetch: refetchTitleConversationStatuses,
	} = useTitleConversationStatuses(
		communityId ?? 0,
		skus,
		CacheKeysEnum.NewReviewCopyConvoStatuses,
	);

	useEffect(() => {
		comments &&
			comments.length > 0 &&
			commentsDataStore.changeConversationStatus(
				comments[0].communityId,
				ConversationStatus.read,
			);
		markAsRead();

		return () => {
			refreshAfterViewing();
			conversationsDataStore && conversationsDataStore.refresh(true);
			communityReviewsDataStore &&
				communityReviewsDataStore.fetchConversationStatus();
			refetchTitleConversationStatuses();
			requestsDataStore && requestsDataStore.fetchConversationStatus();
		};
	}, []);

	const renderAvatar = (comment, size) => {
		return (
			<React.Fragment>
				{comment.commentStatus != ConversationStatus.deleted ? (
					<React.Fragment>
						{renderUserAvatar(comment.userAvatar, size)}
					</React.Fragment>
				) : (
					<React.Fragment>{renderDeletedAvatar(size)}</React.Fragment>
				)}
			</React.Fragment>
		);
	};

	const renderUserAvatar = (userAvatar, size) => {
		return (
			<UserAvatar
				userInitials={userAvatar && userAvatar.initials}
				avatarUri={(userAvatar && userAvatar.uri) || null}
				displayName={userAvatar && userAvatar.displayName}
				avatarSizeClass={size}
			/>
		);
	};

	const renderDeletedAvatar = size => {
		return (
			<UserAvatar
				userInitials={
					<div className="icon-delete-icon text-x-large"></div>
				}
				avatarUri={null}
				avatarSizeClass={size}
			/>
		);
	};

	const parentComment = comments.filter(com => !com.parentCommentId)[0];

	const childComments = comments.filter(com => com.parentCommentId);

	return (
		<div className="parent-frame">
			{parentComment && (
				<React.Fragment>
					<div className="parent-avatar">
						{parentComment &&
							renderAvatar(parentComment, UserAvatar.medium)}
					</div>
					<div className="thread-body">
						<div className="parent-body">
							{parentComment && (
								<Comment comment={parentComment} />
							)}
						</div>
						<div>
							{childComments &&
								childComments.length > 0 &&
								childComments.map(comment => (
									<div
										className="child-frame"
										key={comment.id}>
										<div className="child-avatar">
											{renderAvatar(
												comment,
												UserAvatar.small,
											)}
										</div>
										<div className="child-body">
											<Comment comment={comment} />
										</div>
									</div>
								))}
						</div>
						{parentComment.commentStatus !=
							ConversationStatus.deleted && (
							<div
								style={{
									borderBottom: '1px solid #d2d6d8',
								}}>
								<CommentInputFrame
									parentComment={parentComment}
									childCount={comments.length}
								/>
							</div>
						)}
					</div>
				</React.Fragment>
			)}
		</div>
	);
}
